import { useTranslation } from "react-i18next";
import { DataTable } from "../../components/table/DataTable";
import { useCallback, useState } from "react";
import Form from "../../components/form/Form";
import { Modal } from "../../components/modal/Modal";
import {
  emptyTopicPartQuestion,
  TopicPartQuestion,
  TopicPartQuestionFormData,
} from "../../../core/topicPartQuestion/entities/TopicPartQuestion";
import { useTopicPartQuestionFormValidation } from "../../../core/hooks/form/useTopicPartQuestionFormValidation";
import topicPartQuestionColumns from "../config/topicPartQuestion/topicPartQuestionTableConfig";
import topicPartQuestionFormConfig from "../config/topicPartQuestion/topicPartQuestionFormConfig";
import { useQuery } from "@tanstack/react-query";
import { getAllTopicPartQuestionsUseCase } from "../../../core/topicPartQuestion/useCases/getAllTopicPartQuestionsUseCase";
import { deleteTopicPartQuestionUseCase } from "../../../core/topicPartQuestion/useCases/deleteTopicPartQuestionUseCase";
import { addUpdateTopicPartQuestionUseCase } from "../../../core/topicPartQuestion/useCases/addUpdateTopicPartQuestionUseCase";
import { updateTopicPartQuestionOrderUseCase } from "../../../core/topicPartQuestion/useCases/updateTopicPartQuestionOrderUseCase";
import { useAdminPermissions } from "../../../core/hooks/useAdminPermissions";
import { HistoryButton } from "../../components/history/HistoryButton";

interface TopicPartQuestionListProps {
  topicPartId: string | number;
}

export default function TopicPartQuestionList({
  topicPartId,
}: TopicPartQuestionListProps) {
  const { t } = useTranslation();
  const permissions = useAdminPermissions();
  const [selected, setSelected] = useState<TopicPartQuestion | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["topic-part-questions", topicPartId],
    queryFn: async () => {
      return await getAllTopicPartQuestionsUseCase.exec(topicPartId);
    },
  });

  const handleReorder = useCallback(
    async (list: TopicPartQuestion[]) => {
      const success = await updateTopicPartQuestionOrderUseCase.exec(list);
      if (success) refetch();
    },
    [refetch]
  );

  const form = useTopicPartQuestionFormValidation(selected, t);

  return isLoading ? null : (
    <>
      <DataTable
        title={t("topicPartQuestion.title")}
        datas={data ?? []}
        columns={topicPartQuestionColumns(t)}
        filters={["name"]}
        onRowClicked={(row: TopicPartQuestion) => {
          setSelected(row);
        }}
        onAdd={
          permissions.canCreate
            ? () =>
                setSelected(
                  emptyTopicPartQuestion(
                    topicPartId as string,
                  ) as TopicPartQuestion,
                )
            : undefined
        }
        onReorder={permissions.canReorder ? handleReorder : undefined}
      />

      <Modal
        isOpen={!!selected}
        onClose={() => {
          setSelected(null);
        }}
        title={selected?.id ? t("form.edit") : t("form.add")}
        actions={
          selected?.id ? (
            <HistoryButton
              collection="topicPartQuestions"
              recordId={selected.id}
              recordLabel={selected.text}
              onRestored={() => {
                setSelected(null);
                refetch();
              }}
            />
          ) : undefined
        }
      >
        <Form
          className="md:min-w-[450px]"
          id={selected?.id}
          onSave={async (
            data: TopicPartQuestionFormData,
            id: string | number | null
          ) => {
            const success = await addUpdateTopicPartQuestionUseCase.exec(
              data as TopicPartQuestionFormData,
              id
            );
            if (success) {
              setSelected(null);
              refetch();
            }
          }}
          onDelete={async (id: string | number) => {
            const success = await deleteTopicPartQuestionUseCase.exec(id);
            if (success) {
              setSelected(null);
              refetch();
            }
          }}
          form={form}
          formConfig={topicPartQuestionFormConfig}
          onCancel={() => setSelected(null)}
        />
      </Modal>
    </>
  );
}
